import React, { useEffect, useRef, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { supabase } from './lib/supabase'
import Login from './pages/Login'
import Register from './pages/Register'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import ProfileForm from './pages/ProfileForm'
import Dashboard from './pages/Dashboard'
import ProfileView from './pages/ProfileView'
import './App.css'

function App() {
  const [session, setSession]       = useState(null)
  const [isAdmin, setIsAdmin]       = useState(false)
  const [loading, setLoading]       = useState(true)
  const [showEscape, setShowEscape] = useState(false)
  const isAdminRef = useRef(false)

  const checkAdmin = async (session) => {
    if (!session) { isAdminRef.current = false; setIsAdmin(false); return }
    try {
      const rpcCall = supabase.rpc('is_admin')
      const fallback = new Promise(resolve => setTimeout(() => resolve({ data: null }), 4000))
      const { data } = await Promise.race([rpcCall, fallback])
      isAdminRef.current = !!data
      setIsAdmin(!!data)
    } catch {
      isAdminRef.current = false
      setIsAdmin(false)
    }
  }

  useEffect(() => {
    const escapeTimer = setTimeout(() => setShowEscape(true), 10000)
    const timeout    = setTimeout(() => setLoading(false), 8000)

    supabase.auth.getSession()
      .then(async ({ data: { session } }) => {
        setSession(session)
        await checkAdmin(session)
      })
      .catch(() => {})
      .finally(() => {
        clearTimeout(timeout)
        clearTimeout(escapeTimer)
        setLoading(false)
      })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session)
      // Only re-check admin on a fresh sign-in, not cross-tab token refreshes.
      // If already confirmed as admin, skip — avoids the 4s timeout firing mid-session.
      if (event === 'SIGNED_IN' && !isAdminRef.current) await checkAdmin(session)
      if (event === 'SIGNED_OUT') { isAdminRef.current = false; setIsAdmin(false) }
    })
    return () => subscription.unsubscribe()
  }, [])

  if (loading) return (
    <div className="loading-screen">
      <div className="uplift-logo-mark">↑</div>
      <p>Loading Uplift…</p>
      {showEscape && (
        <button
          onClick={() => { localStorage.clear(); window.location.replace('/login') }}
          style={{ marginTop: 24, background: 'none', border: 'none', color: '#94a3b8', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline' }}
        >
          Taking too long? Click here to sign out
        </button>
      )}
    </div>
  )

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login"           element={!session ? <Login /> : <Navigate to={isAdmin ? '/dashboard' : '/profile'} />} />
        <Route path="/register"        element={!session ? <Register /> : <Navigate to="/profile" />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password"  element={<ResetPassword />} />

        <Route path="/profile"          element={session && !isAdmin ? <ProfileForm session={session} /> : <Navigate to="/login" />} />
        <Route path="/dashboard"        element={session && isAdmin  ? <Dashboard session={session} /> : <Navigate to="/login" />} />
        <Route path="/admin/profile/:id" element={session ? <ProfileView /> : <Navigate to="/login" />} />

        <Route path="*" element={<Navigate to={session ? (isAdmin ? '/dashboard' : '/profile') : '/login'} />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
